import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { REBILL_PUBLIC_KEY, REBILL_PLAN_IDS } from '../config';
import { ensureRebillSDK } from '../lib/rebill';

// Reemplaza a MercadoPagoCardPayment para clientes AR: renderiza el checkout
// embebido de Rebill (`<rebill-checkout>`). La pantalla de Checkout mantiene su
// propio detalle de precio, así que el resumen de Rebill va oculto por CSS —
// pero MONTADO, porque es lo único que auto-aplica el cupón (ver REBILL_STYLE_CSS).
// El backend procesa el webhook (activar plan, cancelar la sub vieja); el front
// solo dispara el checkout con la metadata (id_company, user_id).

// CSS inyectado al componente (prop `css`). Truco clave: usamos
// `checkoutSummary: true` para que el `rebill-summary` (y adentro el
// `discount-coupon`) se MONTE en el DOM — es lo único que auto-aplica el cupón
// (`coupon-code` → `initialCouponCode` → `componentDidLoad` → `setDiscount`).
// El auto-apply corre al montar, NO necesita estar visible; así que ocultamos el
// resumen por CSS (`display:none`, sigue montado) y el precio lo muestra el panel
// del Checkout. Además forzamos el form a ancho completo (una sola columna),
// porque en desktop el SDK arma 2 columnas con min-width 450+350 y se desborda en
// nuestro panel angosto. OJO: depende de clases internas del SDK (light DOM) —
// re-verificar si se actualiza `rebill`.
const REBILL_STYLE_CSS = `
  .rebill-checkout {
    flex-direction: column !important;
    background: transparent !important;
    padding: 0 !important;
    min-height: 0 !important;
  }
  .rebill-checkout .left-section { width: 100% !important; background: transparent !important; }
  .rebill-checkout .left-section-container {
    min-width: 0 !important;
    max-width: none !important;
    width: 100% !important;
    margin: 0.5rem 0 1rem 0 !important;
  }
  /* Resumen de Rebill: montado (para el cupón) pero oculto — el precio lo mostramos nosotros. */
  .rebill-checkout .right-section-checkout,
  .rebill-checkout .summary-mobile-tablet,
  .rebill-checkout .summary-price-details { display: none !important; }
`;

export interface RebillCheckoutResult {
  subscriptionId?: string;
  paymentId?: string;
}

interface RebillCardPaymentProps {
  /** Plan de Atlas: 'emprendimiento' | 'negocio' | 'enterprise1' | ... */
  planId: string;
  /** Código del cupón aplicado (ej. 'DESC30'), o null. Rebill lo auto-aplica. */
  couponCode?: string | null;
  onSuccess: (result: RebillCheckoutResult) => void;
  onError: (message: string) => void;
}

const RebillCardPayment: React.FC<RebillCardPaymentProps> = ({
  planId,
  couponCode,
  onSuccess,
  onError,
}) => {
  const rebillPlanId = REBILL_PLAN_IDS[planId] || '';
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [companyCtx, setCompanyCtx] = useState<{
    idCompany: string;
    userId: string;
  } | null>(null);
  // Prefill del form de Rebill (`customer-information`): los campos visibles
  // arrancan cargados (editables) — el SDK los inyecta en su session store al
  // inicializar. Email = el de la cuenta (además es el que Rebill usa para
  // reconocer clientes existentes / mandar comprobantes).
  const [customerPrefill, setCustomerPrefill] = useState<{
    email?: string;
    fullName?: string;
  } | null>(null);
  const [ctxResolved, setCtxResolved] = useState(false);
  const hostRef = useRef<HTMLDivElement | null>(null);

  const configError = !REBILL_PUBLIC_KEY
    ? 'Falta configurar Rebill (VITE_REBILL_PUBLIC_KEY).'
    : !rebillPlanId
      ? 'Falta configurar el plan de Rebill para este plan.'
      : null;

  // Registro del SDK.
  useEffect(() => {
    if (configError) return;
    let mounted = true;
    ensureRebillSDK()
      .then(() => mounted && setSdkReady(true))
      .catch(
        (err) =>
          mounted && setSdkError((err as Error)?.message ?? 'Error cargando Rebill.'),
      );
    return () => {
      mounted = false;
    };
  }, [configError]);

  // Empresa/usuario logueado → metadata que viaja al webhook.
  useEffect(() => {
    if (configError) {
      setCtxResolved(true);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company, name')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | null;
          if (mounted && idCompany) {
            setCompanyCtx({ idCompany, userId: user.id });
          }
          const fullName = ((profile as any)?.name as string | null)?.trim();
          if (mounted && (user.email || fullName)) {
            setCustomerPrefill({
              ...(user.email ? { email: user.email } : {}),
              ...(fullName ? { fullName } : {}),
            });
          }
        }
      } catch {
        /* sin empresa → seguimos sin metadata (el backend cae al email) */
      } finally {
        if (mounted) setCtxResolved(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [configError]);

  // Montaje imperativo del <rebill-checkout>: seteamos las props como ATRIBUTOS
  // ANTES de insertarlo (el SDK las lee en componentWillLoad al conectarse).
  useEffect(() => {
    if (configError || !sdkReady || !ctxResolved) return;
    const host = hostRef.current;
    if (!host) return;

    const el = document.createElement('rebill-checkout') as any;
    el.setAttribute('public-key', REBILL_PUBLIC_KEY);
    el.setAttribute('plan-id', rebillPlanId);
    el.setAttribute('language', 'es');
    // checkoutSummary:true → el resumen se MONTA (necesario para que el cupón se
    // auto-aplique), pero lo ocultamos por CSS (ver REBILL_STYLE_CSS). El precio
    // lo muestra el panel del Checkout; el form queda a todo el ancho.
    // excludePaymentMethods: sin transferencia bancaria — una transferencia no
    // deja método guardado → sin débito automático de renovación (y el QR es
    // mala UX en mobile). Solo tarjeta.
    el.setAttribute(
      'display',
      JSON.stringify({
        logo: false,
        footer: false,
        checkoutSummary: true,
        excludePaymentMethods: ['bank_transfer'],
      }),
    );
    // Estilos para que combine con el panel del Checkout (fondo parejo, menos
    // espacio arriba). Ver REBILL_STYLE_CSS.
    el.setAttribute('css', REBILL_STYLE_CSS);
    // metadata para correlacionar el pago con la empresa. `customer: {}` es
    // obligatorio: sin él el SDK crashea en setPayment (accede customer.email).
    if (companyCtx) {
      el.setAttribute(
        'external-payment',
        JSON.stringify({
          customer: {},
          metadata: {
            id_company: companyCtx.idCompany,
            user_id: companyCtx.userId,
          },
        }),
      );
    }
    if (couponCode) el.setAttribute('coupon-code', couponCode);
    // Autocompletar nombre/email en el form (visibles y editables).
    if (customerPrefill) {
      el.setAttribute('customer-information', JSON.stringify(customerPrefill));
    }

    const onSuccessEv = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const r = detail?.data?.result ?? detail?.result ?? {};
      onSuccess({ subscriptionId: r.subscriptionId, paymentId: r.paymentId });
    };
    const onErrorEv = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      onError(
        (detail && (detail.message || detail.error)) ||
          'No pudimos procesar el pago. Probá con otra tarjeta.',
      );
    };
    // Salvaguarda: si esperábamos un cupón y Rebill lo rechazó (`discountApplied:
    // false` burbujea del discount-coupon interno), avisamos — si no, el panel
    // muestra precio con descuento pero el cobro saldría al precio pleno (el
    // código no existe/matchea en el panel de Rebill).
    const onDiscountEv = (e: Event) => {
      if (couponCode && (e as CustomEvent).detail === false) {
        onError(
          'No pudimos aplicar tu descuento. Escribinos antes de pagar para que lo revisemos.',
        );
      }
    };
    el.addEventListener('success', onSuccessEv);
    el.addEventListener('error', onErrorEv);
    el.addEventListener('discountApplied', onDiscountEv);
    host.appendChild(el);

    return () => {
      el.removeEventListener('success', onSuccessEv);
      el.removeEventListener('error', onErrorEv);
      el.removeEventListener('discountApplied', onDiscountEv);
      if (el.parentNode === host) host.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkReady, ctxResolved, rebillPlanId, couponCode, companyCtx, customerPrefill]);

  if (configError) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{configError}</span>
      </div>
    );
  }
  if (sdkError) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>No se pudo cargar Rebill. {sdkError}</span>
      </div>
    );
  }
  if (!sdkReady || !ctxResolved) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando checkout…
      </div>
    );
  }
  return <div ref={hostRef} />;
};

export default RebillCardPayment;
